import { useState } from "react";
import { useNavigate } from "react-router";
import { ApiError } from "../../lib/api.js";
import { useBusiness } from "../../lib/business.js";
import { useMe } from "../auth/api.js";
import styles from "./AutomationsScreen.module.css";
import { type AutomationCard, useAutomations, useToggleAutomation } from "./api.js";

/** category → { label, chip-kind } */
const CATEGORY: Record<AutomationCard["category"], { label: string; kind: string }> = {
  utility: { label: "Utility", kind: "muted" },
  marketing: { label: "Marketing", kind: "info" },
  bot: { label: "Bot", kind: "ai" },
};

/** bot_action key → human label (prototype copy). */
const BOT_ACTION_LABEL: Record<string, string> = {
  order_lookup: "Live order lookup",
  cod_check: "COD availability reply",
  welcome_optin: "Welcome + opt-in ask",
};

const cx = (...parts: (string | false | undefined)[]) => parts.filter(Boolean).join(" ");

/** Trigger → action arrow (ported I2.arrowR, viewBox 0 0 20 20). */
const arrowR = (
  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path
      d="M4 10h11M11 5.5l4.5 4.5L11 14.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/** Template deep-link glyph (ported I.templates, viewBox 0 0 20 20). */
const templatesIcon = (
  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <rect x="3" y="3.5" width="14" height="13" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
    <path
      d="M3 7.5h14M6.5 11h7M6.5 13.5h4"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
    />
  </svg>
);

/** Mono "source" line under the trigger node, derived from trigger type + store scope. */
function sourceLabel(a: AutomationCard): string {
  if (a.triggerType === "inbound_intent" || a.triggerType === "first_contact")
    return "Inbound message";
  // order_created / order_fulfilled / cart_abandoned → the store(s) it listens to
  if (a.storeScope && a.storeScope.length > 0) return a.storeScope.join(" + ");
  return "Store order";
}

/** Human action label for the action node. */
function actionLabel(a: AutomationCard): string {
  if (a.actionKind === "bot")
    return (a.botAction && BOT_ACTION_LABEL[a.botAction]) ?? a.botAction ?? "Bot reply";
  return a.templateName ?? a.name;
}

/** "last run 2 min ago" style relative formatting from an ISO timestamp. */
function relativeTime(iso: string): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return "—";
  const diff = Date.now() - then;
  if (diff < 0) return "just now";
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days} d ago`;
  return new Date(then).toLocaleDateString();
}

function AutomationFlowCard({
  a,
  canManage,
  pending,
  onToggle,
  onOpenTemplate,
}: {
  a: AutomationCard;
  canManage: boolean;
  pending: boolean;
  onToggle: (a: AutomationCard) => void;
  onOpenTemplate: () => void;
}) {
  const cat = CATEGORY[a.category] ?? { label: a.category, kind: "muted" };
  const isTemplate = a.actionKind === "template";
  const last = a.enabled
    ? a.lastRunAt
      ? `last run ${relativeTime(a.lastRunAt)}`
      : "last run —"
    : "paused";

  return (
    <div className={cx(styles.autoCard, !a.enabled && styles.off)}>
      <div className={styles.autoCardH}>
        <span className={styles.autoName}>{a.name}</span>
        <button
          type="button"
          role="switch"
          aria-checked={a.enabled}
          aria-label={`Toggle ${a.name}`}
          disabled={!canManage || pending}
          className={cx(styles.tg, a.enabled && styles.on)}
          onClick={() => onToggle(a)}
        >
          <span className={styles.tgThumb} />
        </button>
      </div>

      <div className={styles.autoCardMeta}>
        <span className={cx(styles.autoCat, styles[cat.kind])}>{cat.label}</span>
        <span className={cx(styles.autoState, a.enabled ? styles.on : styles.off)}>
          {a.enabled ? "Active" : "Off"}
        </span>
      </div>

      <div className={styles.autoFlow}>
        <div className={styles.aflNode}>
          <span className={styles.aflK}>When</span>
          <span className={styles.aflV}>{a.triggerLabel}</span>
          <span className={cx(styles.aflSrc, styles.mono)}>{sourceLabel(a)}</span>
        </div>
        <span className={styles.aflArrow}>{arrowR}</span>
        <div className={cx(styles.aflNode, styles.action)}>
          <span className={styles.aflK}>{a.actionKind === "bot" ? "Bot replies" : "Send"}</span>
          <span className={styles.aflV}>{actionLabel(a)}</span>
          {isTemplate ? (
            <button type="button" className={styles.aflLink} onClick={onOpenTemplate}>
              {templatesIcon} template
            </button>
          ) : (
            <span className={cx(styles.aflSrc, styles.mono)}>auto-reply</span>
          )}
        </div>
      </div>

      <div className={styles.autoCardFoot}>
        <span className={styles.afootStat}>
          <b className={styles.mono}>{a.runsToday}</b> today
        </span>
        <span className={styles.afootDot}>·</span>
        <span className={styles.afootStat}>
          <b className={styles.mono}>{a.runsTotal.toLocaleString()}</b> all time
        </span>
        <span className={cx(styles.afootLast, styles.mono)}>{last}</span>
      </div>
    </div>
  );
}

export function AutomationsScreen() {
  const { activeId } = useBusiness();
  return <AutomationsBody key={activeId} businessId={activeId} />;
}

function AutomationsBody({ businessId }: { businessId: string }) {
  const me = useMe();
  const navigate = useNavigate();
  const automations = useAutomations(businessId);
  const toggle = useToggleAutomation(businessId);
  const canManage = me.data?.role === "admin" || me.data?.role === "maintainer";
  const [error, setError] = useState<string | null>(null);

  const list = automations.data ?? [];
  const activeN = list.filter((a) => a.enabled).length;
  const runsToday = list.reduce((s, a) => s + (a.runsToday || 0), 0);
  const runsTotal = list.reduce((s, a) => s + (a.runsTotal || 0), 0);

  const onToggle = (a: AutomationCard) => {
    setError(null);
    toggle.mutate(
      { id: a.id, enabled: !a.enabled },
      {
        onError: (err) =>
          setError(
            err instanceof ApiError && err.code === "TEMPLATE_NOT_APPROVED"
              ? `“${a.name}” needs its template approved before it can run.`
              : "Could not update automation.",
          ),
      },
    );
  };

  return (
    <section className={cx(styles.wrap, styles.screen)}>
      <div className={styles.screenHead}>
        <div>
          <h1 className={styles.title}>Automations</h1>
          <p className={styles.sub}>Triggered flows · order events &amp; inbound intents</p>
        </div>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      <div className={styles.miniBand}>
        <div className={styles.miniStat}>
          <span className={styles.msK}>Active flows</span>
          <span className={cx(styles.msV, styles.tnum)}>
            {activeN}
            <span className={styles.msOf}> / {list.length}</span>
          </span>
          <span className={styles.msS}>running now</span>
        </div>
        <div className={styles.miniStat}>
          <span className={styles.msK}>Runs today</span>
          <span className={cx(styles.msV, styles.tnum)}>{runsToday}</span>
          <span className={styles.msS}>across all flows</span>
        </div>
        <div className={styles.miniStat}>
          <span className={styles.msK}>Runs · all time</span>
          <span className={cx(styles.msV, styles.tnum)}>{runsTotal.toLocaleString()}</span>
          <span className={styles.msS}>since launch</span>
        </div>
      </div>

      <div className={styles.autoGrid}>
        {list.length === 0 ? (
          <div className={styles.empty}>
            {automations.isLoading ? "Loading automations…" : "No automation flows yet."}
          </div>
        ) : (
          list.map((a) => (
            <AutomationFlowCard
              key={a.id}
              a={a}
              canManage={canManage}
              pending={toggle.isPending}
              onToggle={onToggle}
              onOpenTemplate={() => navigate("/templates")}
            />
          ))
        )}
      </div>
    </section>
  );
}
